import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import { Translate, I18n } from 'react-redux-i18n';
import alertify from 'alertifyjs';
import * as brokerActions from 'redux/actions/brokerActions';
import SyncHistoryModal from './SyncHistoryModal';

const BROKERS = [
    { value: 'tastytrade', label: 'Tastytrade' },
];

const emptyForm = {
    broker: 'tastytrade',
    label: '',
    client_id: '',
    client_secret: '',
    refresh_token: '',
};

const Broker = (props) => {
    const location = useLocation();
    const history = useHistory();
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [syncingKey, setSyncingKey] = useState(null);
    const [connections, setConnections] = useState([]);
    const [activeId, setActiveId] = useState(null);
    const [showNewForm, setShowNewForm] = useState(false);
    const [values, setValues] = useState(emptyForm);
    const [manualAccount, setManualAccount] = useState('');
    const [availableAccounts, setAvailableAccounts] = useState([]);
    const [showAvailable, setShowAvailable] = useState(false);
    const [historyAccountId, setHistoryAccountId] = useState(null);

    const active = connections.find((c) => c.id === activeId) || connections[0] || null;
    const accounts = Array.isArray(active?.accounts) ? active.accounts : [];
    const maxAccounts = active?.max_accounts || 3;
    const canAddAccount = Boolean(active) && accounts.length < maxAccounts;
    const historyAccount = accounts.find((account) => account.id === historyAccountId) || null;

    const applyPayload = useCallback((data) => {
        const list = Array.isArray(data?.connections) ? data.connections : (data?.id ? [data] : []);
        setConnections(list);
        setActiveId((prev) => {
            if (prev && list.some((c) => c.id === prev)) {
                return prev;
            }
            return list[0]?.id || null;
        });
        return list;
    }, []);

    const loadStatus = useCallback(async () => {
        if (!props.user_id) {
            return;
        }
        setLoading(true);
        try {
            const data = await props.loadBroker(props.user_id);
            applyPayload(data);
        } catch (e) {
            alertify.error(e.message || I18n.t('try_later'));
        } finally {
            setLoading(false);
        }
    }, [props, applyPayload]);

    useEffect(() => {
        loadStatus();
    }, [loadStatus]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const oauthResult = params.get('broker_oauth');
        if (!oauthResult) {
            return;
        }

        if (oauthResult === 'success') {
            alertify.success(I18n.t('broker_oauth_success'));
            const connId = params.get('connection_id');
            loadStatus().then(() => {
                if (connId) {
                    setActiveId(Number(connId));
                }
            });
        } else if (oauthResult === 'error') {
            alertify.error(params.get('broker_message') || I18n.t('broker_sync_error'));
        }

        params.delete('broker_oauth');
        params.delete('broker_message');
        params.delete('connection_id');
        const nextSearch = params.toString();
        history.replace({
            pathname: location.pathname,
            search: nextSearch ? `?${nextSearch}` : '',
        });
    }, [location.search, history, location.pathname, loadStatus]);

    useEffect(() => {
        if (!active) {
            return;
        }
        setValues({
            broker: active.broker || 'tastytrade',
            label: active.label || '',
            client_id: active.client_id || '',
            client_secret: '',
            refresh_token: '',
        });
        setManualAccount('');
        setShowAvailable(false);
        setAvailableAccounts([]);
    }, [active?.id]);

    const handleChange = (event) => {
        setValues({
            ...values,
            [event.target.name]: event.target.value,
        });
    };

    const replaceActive = (connection) => {
        setConnections((prev) => {
            const idx = prev.findIndex((c) => c.id === connection.id);
            if (idx === -1) {
                return [...prev, connection];
            }
            const next = [...prev];
            next[idx] = connection;
            return next;
        });
        setActiveId(connection.id);
    };

    const handleCreate = async (event) => {
        event.preventDefault();
        if (!props.user_id || busy) {
            return;
        }
        if (!values.client_id.trim() || !values.client_secret.trim()) {
            alertify.error(I18n.t('broker_credentials_required'));
            return;
        }
        setBusy(true);
        try {
            const data = await props.createBrokerConnection({
                id: props.user_id,
                broker: values.broker,
                label: values.label || undefined,
                client_id: values.client_id,
                client_secret: values.client_secret,
                refresh_token: values.refresh_token || undefined,
            });
            replaceActive(data);
            setShowNewForm(false);
            setValues(emptyForm);
        } catch (e) {
            alertify.error(e.message || I18n.t('try_later'));
        } finally {
            setBusy(false);
        }
    };

    const handleSave = async (event) => {
        event.preventDefault();
        if (!props.user_id || busy || !active) {
            return;
        }

        const needsSecret = !active.has_secret && !values.client_secret;
        const needsRefresh = !active.has_refresh_token && !values.refresh_token;
        if (needsSecret || needsRefresh) {
            alertify.error(I18n.t('broker_credentials_required'));
            return;
        }

        setBusy(true);
        try {
            const data = await props.saveBroker({
                id: props.user_id,
                connection_id: active.id,
                broker: values.broker,
                label: values.label || undefined,
                client_id: values.client_id,
                client_secret: values.client_secret || undefined,
                refresh_token: values.refresh_token || undefined,
                prepare_oauth: !values.refresh_token && !active.has_refresh_token,
            });
            replaceActive(data);
            setValues((prev) => ({ ...prev, client_secret: '', refresh_token: '' }));
        } catch (e) {
            alertify.error(e.message || I18n.t('try_later'));
        } finally {
            setBusy(false);
        }
    };

    const handleOAuthConnect = async () => {
        if (!props.user_id || busy) {
            return;
        }
        if (!values.client_id.trim()) {
            alertify.error(I18n.t('broker_client_id_required'));
            return;
        }
        if (!values.client_secret.trim() && !active?.has_secret) {
            alertify.error(I18n.t('broker_oauth_secret_required'));
            return;
        }

        setBusy(true);
        try {
            const data = await props.startBrokerOAuth({
                id: props.user_id,
                connection_id: showNewForm ? undefined : active?.id,
                label: values.label || undefined,
                client_id: values.client_id,
                client_secret: values.client_secret || undefined,
            });
            if (data?.authorize_url) {
                window.location.href = data.authorize_url;
                return;
            }
            alertify.error(I18n.t('try_later'));
        } catch (e) {
            alertify.error(e.message || I18n.t('try_later'));
        } finally {
            setBusy(false);
        }
    };

    const handleDeleteConnection = async () => {
        if (!props.user_id || busy || !active) {
            return;
        }
        setBusy(true);
        try {
            const data = await props.deleteBrokerConnection(props.user_id, active.id);
            applyPayload(data);
            setShowNewForm(false);
        } catch (e) {
            alertify.error(e.message || I18n.t('try_later'));
        } finally {
            setBusy(false);
        }
    };

    const handleAddManualAccount = async () => {
        if (!props.user_id || busy || !active || !manualAccount.trim()) {
            return;
        }
        if (!canAddAccount) {
            alertify.error(I18n.t('broker_accounts_max'));
            return;
        }
        setBusy(true);
        try {
            const data = await props.addBrokerAccount({
                id: props.user_id,
                connection_id: active.id,
                account_number: manualAccount.trim(),
            });
            replaceActive(data);
            setManualAccount('');
        } catch (e) {
            alertify.error(e.message || I18n.t('try_later'));
        } finally {
            setBusy(false);
        }
    };

    const handleLoadAvailable = async () => {
        if (!props.user_id || busy || !active) {
            return;
        }
        if (!active.configured) {
            alertify.error(I18n.t('broker_configure_first'));
            return;
        }
        setBusy(true);
        try {
            const data = await props.loadAvailableBrokerAccounts(props.user_id, active.id);
            setAvailableAccounts(data.available_accounts || []);
            setShowAvailable(true);
        } catch (e) {
            alertify.error(e.message || I18n.t('try_later'));
        } finally {
            setBusy(false);
        }
    };

    const handleAddFromBroker = async (accountNumber) => {
        if (!props.user_id || busy || !active || !accountNumber) {
            return;
        }
        if (!canAddAccount) {
            alertify.error(I18n.t('broker_accounts_max'));
            return;
        }
        setBusy(true);
        try {
            const data = await props.addBrokerAccount({
                id: props.user_id,
                connection_id: active.id,
                account_number: accountNumber,
            });
            replaceActive(data);
            setAvailableAccounts((prev) =>
                prev.map((a) =>
                    a.account_number === accountNumber ? { ...a, linked: true } : a,
                ),
            );
        } catch (e) {
            alertify.error(e.message || I18n.t('try_later'));
        } finally {
            setBusy(false);
        }
    };

    const handleRemoveAccount = async (accountId) => {
        if (!props.user_id || busy) {
            return;
        }
        setBusy(true);
        try {
            const data = await props.removeBrokerAccount(props.user_id, accountId);
            replaceActive(data);
        } catch (e) {
            alertify.error(e.message || I18n.t('try_later'));
        } finally {
            setBusy(false);
        }
    };

    const handleToggleAutoSyncPostmarket = async (account, checked) => {
        if (!props.user_id || busy || !account?.id) {
            return;
        }
        setBusy(true);
        try {
            const data = await props.updateBrokerAccount(account.id, {
                id: props.user_id,
                auto_sync_postmarket: checked,
            });
            replaceActive(data);
            alertify.success(
                checked
                    ? I18n.t('broker_auto_sync_postmarket_enabled')
                    : I18n.t('broker_auto_sync_postmarket_disabled'),
            );
        } catch (e) {
            alertify.error(e.message || I18n.t('try_later'));
        } finally {
            setBusy(false);
        }
    };

    const handleSyncAccount = async (accountNumber) => {
        if (!props.user_id || busy || !active) {
            return;
        }
        if (!active.configured) {
            alertify.error(I18n.t('broker_configure_first'));
            return;
        }
        setBusy(true);
        setSyncingKey(`${active.id}:${accountNumber}`);
        alertify.message(I18n.t('broker_sync_in_progress'));
        try {
            await props.syncBroker({
                id: props.user_id,
                connection_id: active.id,
                account_number: accountNumber,
            });
            await loadStatus();
        } catch (e) {
            alertify.error(e.message || I18n.t('try_later'));
        } finally {
            setBusy(false);
            setSyncingKey(null);
        }
    };

    const handleSyncAll = async () => {
        if (!props.user_id || busy || !active) {
            return;
        }
        if (!active.configured) {
            alertify.error(I18n.t('broker_configure_first'));
            return;
        }
        if (!accounts.length) {
            alertify.error(I18n.t('broker_accounts_required'));
            return;
        }
        setBusy(true);
        setSyncingKey(`${active.id}:all`);
        alertify.message(I18n.t('broker_sync_in_progress'));
        try {
            await props.syncBroker({
                id: props.user_id,
                connection_id: active.id,
                sync_all: true,
            });
            await loadStatus();
        } catch (e) {
            alertify.error(e.message || I18n.t('try_later'));
        } finally {
            setBusy(false);
            setSyncingKey(null);
        }
    };

    const renderCredentialsForm = (onSubmit, isNew) => (
        <form onSubmit={onSubmit}>
            <div className="settings-field">
                <label htmlFor="broker-label">
                    <Translate value="broker_connection_label" />
                </label>
                <input
                    id="broker-label"
                    name="label"
                    type="text"
                    value={values.label}
                    onChange={handleChange}
                    placeholder={I18n.t('broker_connection_label_placeholder')}
                />
            </div>
            <div className="settings-field">
                <label htmlFor="broker-select">
                    <Translate value="broker_select" />
                </label>
                <select
                    id="broker-select"
                    name="broker"
                    value={values.broker}
                    onChange={handleChange}
                >
                    {BROKERS.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
            </div>
            <div className="settings-field">
                <label htmlFor="broker-client-id">
                    <Translate value="broker_client_id" />
                </label>
                <input
                    id="broker-client-id"
                    name="client_id"
                    type="text"
                    autoComplete="off"
                    value={values.client_id}
                    onChange={handleChange}
                    placeholder="Client ID"
                />
            </div>
            <div className="settings-field">
                <label htmlFor="broker-client-secret">
                    <Translate value="broker_client_secret" />
                </label>
                <input
                    id="broker-client-secret"
                    name="client_secret"
                    type="password"
                    autoComplete="new-password"
                    value={values.client_secret}
                    onChange={handleChange}
                    placeholder={
                        !isNew && active?.has_secret
                            ? active.client_secret_masked || '••••••••'
                            : 'Client Secret'
                    }
                />
            </div>
            <div className="settings-field">
                <label htmlFor="broker-refresh-token">
                    <Translate value="broker_refresh_token" />
                </label>
                <input
                    id="broker-refresh-token"
                    name="refresh_token"
                    type="password"
                    autoComplete="new-password"
                    value={values.refresh_token}
                    onChange={handleChange}
                    placeholder={
                        !isNew && active?.has_refresh_token
                            ? active.refresh_token_masked || '••••••••'
                            : 'Refresh Token'
                    }
                />
                <p className="settings-broker-hint">
                    <Translate value="broker_refresh_help" />
                </p>
            </div>
            <div className="settings-panel-actions settings-broker-actions">
                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleOAuthConnect}
                    disabled={busy || !values.client_id}
                >
                    <Translate value="broker_oauth_connect" />
                </button>
                <button type="submit" className="btn btn-secondary" disabled={busy}>
                    <Translate value={isNew ? 'broker_connection_create' : 'save'} />
                </button>
                {!isNew && (
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={handleDeleteConnection}
                        disabled={busy}
                    >
                        <Translate value="broker_connection_remove" />
                    </button>
                )}
                {isNew && (
                    <button
                        type="button"
                        className="btn btn-text"
                        onClick={() => {
                            setShowNewForm(false);
                            setValues(emptyForm);
                        }}
                        disabled={busy}
                    >
                        <Translate value="cancel" />
                    </button>
                )}
            </div>
        </form>
    );

    if (loading) {
        return (
            <div className="settings-panel">
                <p className="settings-panel-subtitle">...</p>
            </div>
        );
    }

    return (
        <div className="settings-panel">
            <h3 className="settings-panel-title">
                <Translate value="broker" />
            </h3>
            <p className="settings-panel-subtitle">
                <Translate value="broker_subtitle_multi" />
            </p>

            <div className="settings-broker-connections-header">
                <h4><Translate value="broker_connections" /></h4>
                <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    disabled={busy || showNewForm}
                    onClick={() => {
                        setShowNewForm(true);
                        setValues(emptyForm);
                        setActiveId(null);
                    }}
                >
                    <Translate value="broker_connection_add" />
                </button>
            </div>

            {connections.length > 0 && !showNewForm && (
                <div className="settings-broker-connection-tabs">
                    {connections.map((conn) => (
                        <button
                            key={conn.id}
                            type="button"
                            className={`settings-broker-tab${active?.id === conn.id ? ' is-active' : ''}`}
                            onClick={() => setActiveId(conn.id)}
                        >
                            {conn.label || conn.broker || `#${conn.id}`}
                            {conn.configured ? '' : ' •'}
                        </button>
                    ))}
                </div>
            )}

            {showNewForm && (
                <div className="settings-broker-connection-card">
                    <h5><Translate value="broker_connection_new" /></h5>
                    {renderCredentialsForm(handleCreate, true)}
                </div>
            )}

            {!showNewForm && !active && (
                <p className="settings-broker-hint">
                    <Translate value="broker_connections_empty" />
                </p>
            )}

            {!showNewForm && active && (
                <>
                    {active.configured && active.last_sync_status !== 'error' && (
                        <div className="settings-broker-status settings-broker-status--ok">
                            <Translate value="broker_connected" />
                        </div>
                    )}
                    {active.last_sync_status === 'error' && (
                        <div className="settings-broker-status settings-broker-status--error">
                            <Translate value="broker_sync_error" />
                        </div>
                    )}

                    <div className="settings-broker-connection-card">
                        <h5><Translate value="broker_connection_credentials" /></h5>
                        {renderCredentialsForm(handleSave, false)}
                    </div>

                    <div className="settings-broker-accounts">
                        <div className="settings-broker-accounts-header">
                            <h4>
                                <Translate value="broker_accounts" />
                                {' '}
                                ({accounts.length}/{maxAccounts})
                            </h4>
                            <button
                                type="button"
                                className="btn btn-primary btn-sm"
                                onClick={handleSyncAll}
                                disabled={busy || !active.configured || !accounts.length}
                            >
                                {syncingKey === `${active.id}:all`
                                    ? <Translate value="broker_sync_in_progress_button" />
                                    : <Translate value="broker_sync_all" />}
                            </button>
                        </div>

                        <div className="settings-broker-add-account">
                            <input
                                type="text"
                                value={manualAccount}
                                onChange={(e) => setManualAccount(e.target.value)}
                                placeholder={I18n.t('broker_account_manual_placeholder')}
                                disabled={!canAddAccount || busy}
                            />
                            <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                onClick={handleAddManualAccount}
                                disabled={!canAddAccount || busy || !manualAccount.trim()}
                            >
                                <Translate value="broker_account_add" />
                            </button>
                            <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                onClick={handleLoadAvailable}
                                disabled={!active.configured || busy || !canAddAccount}
                            >
                                <Translate value="broker_account_load_from_broker" />
                            </button>
                        </div>

                        {showAvailable && (
                            <div className="settings-broker-available">
                                <div className="settings-broker-available-header">
                                    <h6><Translate value="broker_available_accounts" /></h6>
                                    <button
                                        type="button"
                                        className="btn btn-text btn-sm"
                                        onClick={() => setShowAvailable(false)}
                                    >
                                        <Translate value="close" />
                                    </button>
                                </div>
                                {availableAccounts.length === 0 ? (
                                    <p className="settings-broker-hint">
                                        <Translate value="broker_no_available_accounts" />
                                    </p>
                                ) : (
                                    <ul className="settings-broker-available-list">
                                        {availableAccounts.map((acc) => (
                                            <li key={acc.account_number}>
                                                <span>
                                                    {acc.account_number}
                                                    {acc.nickname ? ` — ${acc.nickname}` : ''}
                                                </span>
                                                {acc.linked ? (
                                                    <span className="settings-broker-linked">
                                                        <Translate value="broker_account_linked" />
                                                    </span>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        className="btn btn-primary btn-sm"
                                                        disabled={!canAddAccount || busy}
                                                        onClick={() => handleAddFromBroker(acc.account_number)}
                                                    >
                                                        <Translate value="broker_account_add" />
                                                    </button>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        )}

                        {accounts.length === 0 ? (
                            <p className="settings-broker-hint">
                                <Translate value="broker_accounts_empty" />
                            </p>
                        ) : (
                            accounts.map((account) => (
                                <div key={account.id} className="settings-broker-account-card">
                                    <div className="settings-broker-account-card-header">
                                        <div className="settings-broker-account-title">
                                            <strong>{account.account_number}</strong>
                                        </div>
                                        <div className="settings-broker-account-actions">
                                            <button
                                                type="button"
                                                className="btn btn-primary btn-sm"
                                                onClick={() => handleSyncAccount(account.account_number)}
                                                disabled={busy || !active.configured}
                                            >
                                                {syncingKey === `${active.id}:${account.account_number}`
                                                    ? <Translate value="broker_sync_in_progress_button" />
                                                    : <Translate value="broker_sync" />}
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-secondary btn-sm"
                                                onClick={() => setHistoryAccountId(account.id)}
                                            >
                                                <Translate value="broker_sync_history" />
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-secondary btn-sm"
                                                onClick={() => handleRemoveAccount(account.id)}
                                                disabled={busy}
                                            >
                                                <Translate value="broker_account_remove" />
                                            </button>
                                        </div>
                                    </div>

                                    <div className="settings-broker-account-meta">
                                        <div className="settings-broker-account-meta-row">
                                            <span className="settings-broker-account-meta-label">
                                                <Translate value="broker_last_sync" />
                                            </span>
                                            <span className="settings-broker-account-meta-value">
                                                {account.last_sync_at
                                                    ? new Date(account.last_sync_at).toLocaleString()
                                                    : '—'}
                                            </span>
                                        </div>
                                        <div className="settings-broker-account-meta-row">
                                            <span className="settings-broker-account-meta-label">
                                                <Translate value="broker_sync_window" />
                                            </span>
                                            <span className="settings-broker-account-meta-value">
                                                {account.last_sync_from
                                                    ? `${account.last_sync_from} → ${account.last_sync_to || '—'}`
                                                    : '—'}
                                            </span>
                                        </div>
                                        {account.last_sync_message && (
                                            <div
                                                className={
                                                    account.last_sync_status === 'error'
                                                        ? 'settings-broker-account-meta-row settings-broker-account-meta-row--error'
                                                        : 'settings-broker-account-meta-row'
                                                }
                                            >
                                                <span className="settings-broker-account-meta-label">
                                                    <Translate value="broker_sync_status" />
                                                </span>
                                                <span className="settings-broker-account-meta-value">
                                                    {account.last_sync_message}
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    <div className="settings-broker-auto-sync">
                                        <label className="settings-broker-auto-sync-label">
                                            <input
                                                type="checkbox"
                                                checked={Boolean(account.auto_sync_postmarket)}
                                                disabled={busy || !active.configured}
                                                onChange={(e) => handleToggleAutoSyncPostmarket(
                                                    account,
                                                    e.target.checked,
                                                )}
                                            />
                                            <span className="settings-broker-auto-sync-copy">
                                                <span className="settings-broker-auto-sync-title">
                                                    <Translate value="broker_auto_sync_postmarket" />
                                                </span>
                                                <span className="settings-broker-auto-sync-hint">
                                                    <Translate value="broker_auto_sync_postmarket_hint" />
                                                </span>
                                            </span>
                                        </label>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    <SyncHistoryModal
                        open={Boolean(historyAccount)}
                        account={historyAccount}
                        onClose={() => setHistoryAccountId(null)}
                    />
                </>
            )}
        </div>
    );
};

Broker.propTypes = {
    className: PropTypes.string,
};

function mapStateToProps(reducer) {
    return {
        user_id: reducer.session.auth.id,
    };
}

const mapDispatchToProps = (dispatch) => ({
    loadBroker: (userId) => dispatch(brokerActions.loadBroker(userId)),
    createBrokerConnection: (params) => dispatch(brokerActions.createBrokerConnection(params)),
    saveBroker: (params) => dispatch(brokerActions.saveBroker(params)),
    deleteBrokerConnection: (userId, connectionId) =>
        dispatch(brokerActions.deleteBrokerConnection(userId, connectionId)),
    startBrokerOAuth: (params) => dispatch(brokerActions.startBrokerOAuth(params)),
    syncBroker: (params) => dispatch(brokerActions.syncBroker(params)),
    addBrokerAccount: (params) => dispatch(brokerActions.addBrokerAccount(params)),
    updateBrokerAccount: (accountId, params) =>
        dispatch(brokerActions.updateBrokerAccount(accountId, params)),
    removeBrokerAccount: (userId, accountId) =>
        dispatch(brokerActions.removeBrokerAccount(userId, accountId)),
    loadAvailableBrokerAccounts: (userId, connectionId) =>
        dispatch(brokerActions.loadAvailableBrokerAccounts(userId, connectionId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Broker);
